import React, { useCallback, useMemo } from 'react';
import { type Href, Link, usePathname, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';

import { ScreenHeader as GarnishScreenHeader, useIsFocused } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useLoyaltyAvailablePoints } from '../../hooks/useLoyaltyAvailablePoints';
import { OrderStatusBar } from '../OrderStatusBar';
import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from './graphql/ScreenHeader.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeader = (props: Props) => {
  const { disclaimer, pendingOrder, rounded } = props;

  const pathname = usePathname();
  const router = useRouter();
  const { track } = useTelemetry();
  const { formatMessage, formatNumber } = useIntl();

  const isLoggedIn = useIsLoggedIn();
  const lastInteractedStore = useLastInteractedStore();
  const points = useLoyaltyAvailablePoints();
  const isFocused = useIsFocused();

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData, fetching: isFetchingAddress }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData, fetching: isFetchingRestaurant }] = useScreenHeaderRestaurantQuery(
    {
      variables: { id: restaurantId },
      pause: !restaurantId,
      requestPolicy: 'cache-first',
    },
  );
  // ─── Derived ───────────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);
  const headerEyebrow = formatMessage(messages.headerEyebrow, { orderChannel });

  const locationSelectionHref = useMemo<Href>(
    () => ({ pathname: '/location-selection', params: { backToPath: pathname } }),
    [pathname],
  );

  // NOTE: Custom light status bar is needed only on Android. iOS handles color change automatically.
  const shouldRenderCustomStatusBar = Platform.OS === 'android' && isFocused;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLocationPress = useCallback(() => {
    track('header_location_selected');
    router.navigate(locationSelectionHref);
  }, [locationSelectionHref, router, track]);

  const handleRewardsPress = useCallback(() => {
    track('header_points_cta_selected');

    if (isLoggedIn) {
      router.navigate('/loyalty');

      return;
    }

    router.navigate({
      pathname: '/(auth)/join-or-sign-in',
      params: { redirect: '/' },
    });
  }, [isLoggedIn, router, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      {shouldRenderCustomStatusBar ? <OrderStatusBar barStyle="light-content" /> : null}

      <GarnishScreenHeader.Container palette={pendingOrder ? 'spinach' : 'brown'} rounded={rounded}>
        <GarnishScreenHeader.Content>
          {pendingOrder ? (
            <Link asChild href={pendingOrder.href}>
              <TouchableOpacity
                accessibilityLabel={`${pendingOrder.statusLabel}, ${pendingOrder.etaLabel}`}
                accessibilityRole="button"
                activeOpacity={0.8}
                onPress={pendingOrder.onPress}
                style={styles.pendingOrderButton}
              >
                <GarnishScreenHeader.Eyebrow>
                  {pendingOrder.statusLabel}
                </GarnishScreenHeader.Eyebrow>

                <GarnishScreenHeader.Title>{pendingOrder.etaLabel}</GarnishScreenHeader.Title>
              </TouchableOpacity>
            </Link>
          ) : (
            <GarnishScreenHeader.TitleContainer>
              {lastInteractedStore ? (
                <GarnishScreenHeader.Eyebrow>{headerEyebrow}</GarnishScreenHeader.Eyebrow>
              ) : null}

              {isFetchingAddress || isFetchingRestaurant ? null : (
                <GarnishScreenHeader.LocationButton onPress={handleLocationPress}>
                  {locationTitle}
                </GarnishScreenHeader.LocationButton>
              )}
            </GarnishScreenHeader.TitleContainer>
          )}

          {isLoggedIn ? (
            <GarnishScreenHeader.PointsButton
              points={formatNumber(points)}
              text={formatMessage(messages.points)}
              onPress={handleRewardsPress}
            />
          ) : (
            <GarnishScreenHeader.PointsButton
              text={formatMessage(messages.signIn)}
              onPress={handleRewardsPress}
            />
          )}
        </GarnishScreenHeader.Content>
      </GarnishScreenHeader.Container>

      {disclaimer ? (
        <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} rounded={rounded} />
      ) : null}
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pendingOrderButton: {
    flexShrink: 1,
    alignItems: 'flex-start',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  headerEyebrow: {
    defaultMessage:
      '{orderChannel, select, delivery {Deliver to} pickup {Pickup from} outpost {Outpost at} other {Deliver to}}',
    description: 'Screen header | Location eyebrow',
  },
  findLocationNearYou: {
    defaultMessage: 'Find location near you',
    description: 'Screen header | Find location near you button',
  },
  points: {
    defaultMessage: 'pts',
    description: 'Screen header | Points button',
  },
  signIn: {
    defaultMessage: 'Sign in',
    description: 'Screen header | Sign in button',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  disclaimer?: string;
  pendingOrder?: PendingOrder;
  rounded?: boolean;
};

type PendingOrder = {
  etaLabel: string;
  href: Href;
  statusLabel: string;
  onPress: () => void;
};
